<template>
  <div v-loading="isLoading" style="width: 100%; height: 100%;">
    <div class="search-area">
      <w-search-field @search="onSearch"/>
    </div>
    <el-scrollbar ref="scroll" class="scrollbar-area">
      <drag :transfer-data="getTransferData()" :image-x-offset="-20">
        <template slot="image"><div class="managed-object-tree-drag-image">{{ `${Object.keys(selectedNodes).length} 개가 선택되었습니다.` }}</div></template>
        <el-tree
          class="managed-object-tree tree-w-stroke obj-tree"
          :ref="treeRef"
          :data="treeData"
          :filter-node-method="filterNode"
          :default-expand-all="true"
          :props="defaultProps"
          :node-key="defaultProps.treeKey"
          :expand-on-click-node="false"
          highlight-current
        >
        <span :class="['el-tree-node__label', (node.selected ? 'selected' : 'unselected')]" slot-scope="{ node, data }" @mousedown="onMousedownNode(node, data, $event)" @click="onClickNode(node, data, $event)"
        :style="{cursor: isVirtualObj(node)? 'default' : 'not-allowed'}" :draggable = "!isVirtualObj(node)"
        >
          <div v-if="data.itemLevel === 'NODE'" >
            <span>
              <span v-if="!isEmpty(data.subType)" class="obj-icon">
                <i :class="getObjTypeIconClass(data)"></i>
              </span>
              <span>{{ data.label }}</span>
            </span>
          </div>
          <span v-else>
            <span class="group">{{ data.label }}</span>
          </span>
        </span>
        </el-tree>
      </drag>
    </el-scrollbar>
  </div>
</template>

<script>
import { isEmpty, forEach, map, has } from 'lodash';
import { Drag } from 'vue-drag-drop';
import { defaultProps, treeType } from '@/constants/tree';
import { getObjTypeIconClass } from '@/utils/tree';
import MapAPI from '@/api/topologymap/common';
import TreeFilter from '@/mixins/tree/filter';
import WSearchField from '@/components/form/SearchField';
import { Q } from '@/views/topologymap/js/maps';

export default {
  name: 'WVirtualObjectElement',
  mixins: [TreeFilter],
  components: {
    Drag,
    WSearchField,
  },
  props: {
    treeTypes: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      treeData: [],
      treeType: treeType.LOGICAL,
      treeRef: `${treeType.LOGICAL}Tree`,
      treeTypesInMenu: this.treeTypes,
      defaultProps,
      selectedNodes: {},
      lastSelectedNode: null,
    };
  },
  mounted() {
    this.$refs.scroll.$el.addEventListener('keydown', this.keyDownControl);

    this.loadData();
  },
  beforeDestroy() {
    this.$refs.scroll.$el.removeEventListener('keydown', this.keyDownControl);
  },
  methods: {
    isEmpty,
    getObjTypeIconClass,
    /**
     * VirtualObj 판단
     * @author JH
     * @param  {Object} node    선택 노드 객체
     */
    isVirtualObj(node) {
      return node.isLeaf && node.data.type === 'virtualObject';
    },
    /**
     * 선택된 노드 초기화
     * @author JH
     */
    clearSelectedNodes() {
      forEach(this.selectedNodes, (selectedNode) => {
        this.$set(selectedNode, 'selected', false);
      });
      this.selectedNodes = {};
    },
    onSearch(value) {
      if (this.$refs[this.treeRef]) {
        this.clearSelectedNodes();
        this.lastSelectedNode = null;

        this.$refs[this.treeRef].filter(value);
      }
    },
    keyDownControl(evt) {
      const keyCode = evt.keyCode;
      const ctrlKey = evt.ctrlKey;
      // Ctrl+A 전체선택
      if (ctrlKey && keyCode === 65) {
        evt.preventDefault();
        this.selectAllNode(this.$refs[this.treeRef].root.childNodes);
      }
    },
    /**
     * 넘겨받은 그룹 하위 노드 전체 선택
     * 펼쳐져 있는 그룹만 해당
     * @author {name}
     * @param  {[type]} childNodes [description]
     * @return {[type]}            [description]
     */
    selectAllNode(childNodes) {
      forEach(childNodes, (childNode) => {
        if (this.isVirtualObj(childNode) && childNode.visible) {
          this.$set(childNode, 'selected', true);
          this.$set(this.selectedNodes, childNode.id, childNode);
        } else if (childNode.childNodes && childNode.expanded) {
          this.selectAllNode(childNode.childNodes);
        }
      });
    },
    /**
     * 마우스다운 이벤트
     * @author {name}
     * @param  {[type]} node  [description]
     * @param  {[type]} data  [description]
     * @param  {[type]} event [description]
     * @return {[type]}       [description]
     */
    onMousedownNode(node, data, event) {
      if (!this.isVirtualObj(node)) {
        event.preventDefault();
        return;
      }
      // 개별 다중 선택
      if (event.ctrlKey) {
        if (has(this.selectedNodes, node.id)) {
          this.$set(node, 'selected', false);
          this.$delete(this.selectedNodes, node.id);
        } else {
          this.$set(node, 'selected', true);
          this.$set(this.selectedNodes, node.id, node);
        }
        this.lastSelectedNode = node;

      // 영역 다중 선택
      } else if (event.shiftKey) {
        if (!this.lastSelectedNode) {
          this.lastSelectedNode = node;
        }

        // 같은 그룹 내에서만 영역 선택 가능하도록하기 위해 다른 그룹을 선택하면 선택 상태를 초기화한다.
        if (this.lastSelectedNode.parent.id !== node.parent.id) {
          this.lastSelectedNode = node;
        }
        this.clearSelectedNodes();

        // 마지막으로 선택 된 노드를 기준으로 위로 영역선택할지, 아래로 영역선택할지 결정
        const direction = node.id > this.lastSelectedNode.id ? 'forward' : 'backward';
        /**
         * 20191220 (JH) 변경사항
         * 검색조건 수용
         */
        // 그룹 내 노드 순환 돌면서 영역 선택
        forEach(node.parent.childNodes, (childNode) => {
          if (direction === 'forward') {
            if (childNode.id >= this.lastSelectedNode.id && childNode.id <= node.id && childNode.visible) {
              this.$set(childNode, 'selected', true);
              this.$set(this.selectedNodes, childNode.id, childNode);
            }
          } else if (direction === 'backward') {
            if (childNode.id <= this.lastSelectedNode.id && childNode.id >= node.id && childNode.visible) {
              this.$set(childNode, 'selected', true);
              this.$set(this.selectedNodes, childNode.id, childNode);
            }
          }
        });

      // 단일 선택
      } else if (!has(this.selectedNodes, node.id)) {
        // 기존에 선택 된 노드들 초기화
        this.clearSelectedNodes();
        this.$set(node, 'selected', true);
        this.$set(this.selectedNodes, node.id, node);
      }
    },
    /**
     * 노드 클릭
     * @author {name}
     * @param  {[type]} node  [description]
     * @param  {[type]} data  [description]
     * @param  {[type]} event [description]
     * @return {[type]}       [description]
     */
    onClickNode(node, data, event) {
      if (!this.isVirtualObj(node)) {
        return;
      }
      if (!event.ctrlKey && !event.shiftKey) {
        this.clearSelectedNodes();
        this.$set(node, 'selected', true);
        this.$set(this.selectedNodes, node.id, node);

        this.lastSelectedNode = node;
      }
    },
    /**
     * 트리 노드 데이터를 조회한다.
     * @author Ju
     * @param  {Object} node    부모 노드 객체
     * @param  {Object} resolve
     */
    loadData() {
      if (this.isLoading === true) {
        return;
      }
      this.isLoading = true;
      MapAPI.getVirtualObjectTree().then((response) => {
        this.treeData = response.data.children;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    /**
     * 드래그 데이터 반환
     * @author {name}
     * @return {[type]} [description]
     */
    getTransferData() {
      const returnObject = {
        type: Q.VirtualObjectModel,
        data: map(this.selectedNodes, (selectedNode) => selectedNode.data),
      };
      return returnObject;
    },
  },
};
</script>
<style lang="scss">
.managed-object-tree {
  .el-tree-node__children > .el-tree-node.is-current > .el-tree-node__content .is-leaf + .el-tree-node__label {
    color: #777;
    font-weight: 500;
  }
  .el-tree-node__label {
    &.selected {
      color: #2b68be !important;
      font-weight: 600 !important;
    }
  }

  .el-tree-node.is-current > .el-tree-node__content .el-tree-node__label .group {
    color: #333;
  }
  .el-tree-node__label .group:hover {
    color: #333;
  }
}
.managed-object-tree-drag-image {
  background: white;
  border: 1px solid #000;
  padding: 2px;
  color: #333;
  font-size: 13px;
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/views/topologymap/components/element/VirtualObjectElement.vue